import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import Button from 'components/Qds/Button';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import Stars from 'components/ui/Stars';
import config from 'config';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCountry } from 'hooks/useCountry';
import { useEffect, useState } from 'react';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { quoteWrap } from 'utils/quoteWrap';
import styles from './GenericCTA.module.scss';
import type { InspectorPropsType } from 'hooks/useLivePreview';
import type { LeadForm } from 'types/LeadForm';
import type { IBlockGenericCtaFields } from 'types/contentful/generated';

type Props = IBlockGenericCtaFields & {
  treatment?: string;
  inspectorProps?: InspectorPropsType;
};

const GenericCTA = ({
  ctaText,
  patientManagerAvatar,
  patientManagerName,
  patientManagerTitle,
  patientManagerMessage,
  reviewStars,
  reviewText,
  reviewAuthor,
  reviewAuthorCountry,
  theme,
  treatment,
  id,
  inspectorProps,
}: Props): JSX.Element => {
  const { country } = useCountry();
  const [data, setData] = useState<Partial<LeadForm> | null>(null);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (!data && treatment) {
      setData({
        treatment,
        skipValidation: true,
      });
    }
  }, []);

  const { phone } = config.countries[country as keyof typeof config.countries];

  const renderReviewSection = (): JSX.Element => {
    return (
      <>
        <Stars className={styles.stars} rating={reviewStars} starSize={13} />
        <span className={styles.review}>
          <span
            className={clsx(
              styles.reviewAuthor,
              'Dark' === theme ? 'text-white' : 'text-grey-darkest',
            )}
            {...inspectorProps?.({ fieldId: 'reviewAuthor' })}
          >
            {reviewAuthor}
          </span>
          {reviewAuthorCountry && (
            <span
              className={clsx('Dark' === theme && 'text-silver-light')}
              {...inspectorProps?.({ fieldId: 'reviewAuthorCountry' })}
            >
              , {reviewAuthorCountry}:
            </span>
          )}
          <br />
          <span
            className={clsx('Dark' === theme && 'text-silver-light')}
            {...inspectorProps?.({ fieldId: 'reviewText' })}
          >
            {quoteWrap(reviewText)}
          </span>
        </span>
      </>
    );
  };

  return (
    <>
      <div id={sanitizeContentfulBlockId(id)} className={styles.container}>
        <div
          className={clsx(
            styles.grid,
            'Dark' === theme ? styles.gridDark : styles.gridLight,
          )}
        >
          <div className={styles.wrapper}>
            <div
              className={styles.patientManager}
              {...inspectorProps?.({ fieldId: 'patientManagerAvatar' })}
            >
              {patientManagerAvatar && (
                <Image
                  src={
                    patientManagerAvatar.fields?.file.url ||
                    // eslint-disable-next-line
                    // @ts-ignore
                    patientManagerAvatar.url
                  }
                  alt={patientManagerName || ''}
                  className={styles.image}
                  height={96}
                  width={96}
                  contentful
                />
              )}
              <span
                className={clsx(
                  styles.patientManagerName,
                  'Dark' === theme ? 'text-white' : 'text-grey-darkest',
                )}
                {...inspectorProps?.({ fieldId: 'patientManagerName' })}
              >
                {patientManagerName}
              </span>
              <span
                className={clsx(
                  styles.patientManagerTitle,
                  'Dark' === theme && 'text-silver-light',
                )}
                {...inspectorProps?.({ fieldId: 'patientManagerTitle' })}
              >
                {patientManagerTitle}
              </span>
            </div>
            <div className={styles.actions}>
              <div
                className={styles.patientManagerMessage}
                {...inspectorProps?.({ fieldId: 'patientManagerMessage' })}
              >
                {patientManagerMessage}
              </div>
              <Link href={`tel:${phone}`}>
                <a
                  className={clsx(
                    styles.phone,
                    'Dark' === theme ? 'text-white' : 'text-grey-darkest',
                  )}
                >
                  {phone}
                </a>
              </Link>
              <ExternalFunnelButton
                funnelId={FunnelId.CONTACT_FORM}
                renderButton={(openFunnel) => {
                  return (
                    <Button
                      className="mt-4"
                      onClick={() => {
                        trackEvent({
                          category: 'CTA',
                          action: 'CTA|Click',
                          label: `CTA|Block: Generic CTA|${id}|${ctaText}`,
                        });

                        openFunnel();
                      }}
                      {...inspectorProps?.({ fieldId: 'ctaText' })}
                    >
                      {ctaText}
                    </Button>
                  );
                }}
              />
            </div>
            <div className={styles.reviewsMobile}>{renderReviewSection()}</div>
          </div>
          <div className={styles.reviewsDesktop}>{renderReviewSection()}</div>
        </div>
      </div>
    </>
  );
};

export default GenericCTA;
